import React from 'react';
import { ChakraProvider, Box } from '@chakra-ui/react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from './hooks/useAuth';
import { DataStoreProvider } from './contexts/DataStoreContext';
import { Navbar } from './components/Layout/Navbar';
import { AuthPage } from './components/Auth/AuthPage';
import { AdminDashboard } from './components/Admin/AdminDashboard';
import { EventList } from './components/User/EventList';
import { EventView } from './components/User/EventView';
import { UserProfile } from './components/User/UserProfile';
import { Leaderboard } from './components/User/Leaderboard';
import { FighterList } from './components/Fighter/FighterList';
import { EventHistory } from './components/Event/EventHistory';
import { LandingPage } from './components/Landing/LandingPage';
import { LoadingScreen } from './components/Common/LoadingScreen';
import { GoogleMapsLoader } from './components/Common/GoogleMapsLoader';
import { NotFound } from './components/Common/NotFound';
import { SlugRedirect } from './components/Common/SlugRedirect';
import ErrorBoundary from './components/Common/ErrorBoundary';
import theme from './theme';

function App() {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <ChakraProvider theme={theme}>
        <LoadingScreen />
      </ChakraProvider>
    );
  }

  return (
    <ChakraProvider theme={theme}>
      <ErrorBoundary>
        <DataStoreProvider>
          <GoogleMapsLoader>
            <Router>
              <Box minH="100vh">
                <Navbar />

                <Routes>
                  {!user ? (
                    <>
                      <Route path="/" element={<LandingPage />} />
                      <Route path="/auth" element={<AuthPage />} />
                      <Route path="/event/:eventId" element={
                        <ErrorBoundary>
                          <EventView />
                        </ErrorBoundary>
                      } />
                      <Route path="/404" element={<NotFound />} />
                      {/* Redirect authenticated-only routes to landing page */}
                      <Route path="/events" element={<Navigate to="/" replace />} />
                      <Route path="/fighters" element={<Navigate to="/" replace />} />
                      <Route path="/history" element={<Navigate to="/" replace />} />
                      <Route path="/leaderboard" element={<Navigate to="/" replace />} />
                      <Route path="/profile" element={<Navigate to="/" replace />} />
                      <Route path="/admin" element={<Navigate to="/" replace />} />
                      {/* Catch-all route for slugs - redirect to /event/:eventSlug */}
                      <Route path="/:eventSlug" element={<SlugRedirect />} />
                      <Route path="*" element={<Navigate to="/" replace />} />
                    </>
                  ) : (
                    <>
                      <Route
                        path="/"
                        element={user?.isAdmin ? <Navigate to="/admin" replace /> : <EventList />}
                      />
                      <Route path="/auth" element={<AuthPage />} />
                      <Route path="/events" element={<EventList />} />
                      <Route path="/event/:eventId" element={
                        <ErrorBoundary>
                          <EventView />
                        </ErrorBoundary>
                      } />
                      <Route path="/fighters" element={
                        <ErrorBoundary>
                          <FighterList />
                        </ErrorBoundary>
                      } />
                      <Route path="/history" element={
                        <ErrorBoundary>
                          <EventHistory />
                        </ErrorBoundary>
                      } />
                      <Route path="/leaderboard" element={
                        <ErrorBoundary>
                          <Leaderboard />
                        </ErrorBoundary>
                      } />
                      <Route path="/profile" element={
                        <ErrorBoundary>
                          <UserProfile userId={user?.uid} />
                        </ErrorBoundary>
                      } />
                      {user?.isAdmin && (
                        <Route path="/admin" element={
                          <ErrorBoundary>
                            <AdminDashboard />
                          </ErrorBoundary>
                        } />
                      )}
                      <Route path="/404" element={<NotFound />} />
                      {/* Catch-all route for slugs - try to load as event */}
                      <Route path="/:eventSlug" element={<SlugRedirect />} />
                      <Route path="*" element={<Navigate to={user?.isAdmin ? "/admin" : "/"} replace />} />
                    </>
                  )}
                </Routes>
              </Box>
            </Router>
          </GoogleMapsLoader>
        </DataStoreProvider>
      </ErrorBoundary>
    </ChakraProvider>
  );
}

export default App;
